import { useRouter } from "next/router";
import React, { useState, useEffect } from "react";
import styled from "styled-components";
import Header from "@/components/main/Header";
import Footer from "@/components/main/Footer";
import ProductItem from "@/components/products/ProductItem";
import { IProductType } from "@/typings/Props";
import useProduct from "@/hooks/useProduct";
import { useTranslation } from "react-i18next";
import { media } from "@/styles/media";

const DEFAULT_SORT_IDX = 0;

export default function Page() {
  const router = useRouter();
  const { t } = useTranslation();
  const products = useProduct();

  const [sortIdx, setSortIdx] = useState(DEFAULT_SORT_IDX);

  useEffect(() => {
    products.fetchProductsAsync();
  }, []);

  const sortOptions: { label: string; sort: (products: IProductType[]) => IProductType[] }[] = [
    {
      label: t("menu.products.products.sort.default-sorting"),
      sort: (products: IProductType[]) =>
        products.sort(
          (a, b) => a.metadata.menu_order - b.metadata.menu_order || a.metadata.title.localeCompare(b.metadata.title)
        ),
    },
    {
      label: t("menu.products.products.sort.sort-by-popularity"),
      sort: (products: IProductType[]) =>
        products.sort((a, b) => a.metadata.sales - b.metadata.sales || b.metadata.id - a.metadata.id),
    },
    {
      label: t("menu.products.products.sort.sort-by-latest"),
      sort: (products: IProductType[]) => products.sort((a, b) => a.metadata.id - b.metadata.id),
    },
    {
      label: t("menu.products.products.sort.sort-by-price-low-to-high"),
      sort: (products: IProductType[]) =>
        products.sort((a, b) => a.metadata.price - b.metadata.price || a.title.localeCompare(b.title)),
    },
    {
      label: t("menu.products.products.sort.sort-by-price-high-to-low"),
      sort: (products: IProductType[]) =>
        products.sort((a, b) => b.metadata.price - a.metadata.price || b.title.localeCompare(a.title)),
    },
  ];

  if (products.state.loading) {
    return <>loading...</>;
  }

  if (products.state.products.length < 1) {
    return <>Products not found.</>;
  }

  const currProducts = [
    { title: t("menu.products.body-composition-analyzers"), products: products.state.products.slice(0, 5) },
    { title: t("menu.products.body-water-analyzers"), products: products.state.products.slice(5, 7) },
    { title: t("menu.products.blood-pressure-monitor"), products: products.state.products.slice(9, 11) },
    { title: t("menu.products.stadiometer"), products: products.state.products.slice(7, 9) },
    { title: t("menu.products.handgrip-strength-dynamometer"), products: products.state.products.slice(11, 12) },
  ][Number(router.query.id)];

  if (!currProducts) {
    return <>Products not found.</>;
  }

  currProducts.products = sortOptions[sortIdx].sort(currProducts.products);

  return (
    <>
      <Header />
      <ContentWrapper>
        <ProductsWrapper>
          <TitleWrapper>
            <Title>{currProducts.title}</Title>
            <ControlPanel>
              <Results>
                {t("menu.products.products.results").replaceAll("{{results}}", String(currProducts.products.length))}
              </Results>
              <SortWrap>
                <Select value={sortIdx} onChange={(e) => setSortIdx(Number(e.currentTarget.value))}>
                  {sortOptions.map((v, i) => (
                    <option key={v.label} value={i}>
                      {v.label}
                    </option>
                  ))}
                </Select>
              </SortWrap>
            </ControlPanel>
          </TitleWrapper>
          <ProductItemsWrapper>
            {currProducts.products.map((data: IProductType) => {
              return <ProductItem {...data} key={data.id} />;
            })}
          </ProductItemsWrapper>
        </ProductsWrapper>
      </ContentWrapper>
      <Footer />
    </>
  );
}

const ContentWrapper = styled.div`
  max-width: 1425px;
  height: auto;
  margin: auto;
  padding: 0 90px;
  display: flex;
  justify-content: center;

  ${media.desktop`
    max-width: 1425px;
    width: 100%;
    height: auto;
    margin: 0 auto;
    padding: 0 90px;
    display: block;
  `}
  ${media.tablet`
    width: 100%;
    margin: 0 auto;
    padding: 0 60px;   
    box-sizing: border-box;
  `}
  ${media.phablet`
    padding: 0 32px;    
  `}
`;

const ControlPanel = styled.div`
  max-width: 1200px;
  width: 50%;
  margin: 0 auto;
  display: flex;
  justify-content: right;
  align-items: center;

  ${media.tablet`
      width: 50%;
    margin: 0 auto;
  `}
  ${media.phablet`
    width: 100%;
  `}
`;

const Results = styled.p`
  margin: 0 50px -5px 0;
  font-family: "Hind";
  font-size: 16px;
  color: #676767;

  ${media.tablet`
    display: none;
  `}
`;

const SortWrap = styled.div`
  display: flex;
  align-items: center;

  ${media.phablet`
    width: 100%;
  `}
`;

const Select = styled.select`
  position: relative;
  height: 40px;
  padding: 0.5rem 1rem 0.35rem;
  margin: 0;
  font-size: 14px;
  color: #555;
  border: 1px solid black;
  border-radius: 10px;
  background-color: rgba(0, 0, 0, 0.035);
  box-shadow: none;
  -webkit-box-shadow: none;
  transition: border-color 0.2s ease;
  -webkit-transition: border-color 0.2s ease;
  appearance: none;

  ${media.phablet`
    width: 100%;
  `}
`;

const ProductsWrapper = styled.div`
  flex: 1;
`;

const Title = styled.p`
  width: 50%;
  font-family: "Lato Bold", sans-serif;
  font-size: 34px;
  font-weight: 700;
  color: #971b2f;

  ${media.desktop`
    font-size: 28.9px;
  `}
  ${media.tablet`
    width: 100%;
    font-size: 27.2px;
  `}
  ${media.mobile`
    width: 100%;
    font-size: 25.5px;
  `}
`;

const TitleWrapper = styled.div`
  padding: 40px 0;
  display: flex;
  flex-direction: row;

  ${media.tablet`
    flex-direction: row;
  `}
  ${media.phablet`
    flex-direction: column;
    gap: 8px;
  `}
`;

const ProductItemsWrapper = styled.div`
  max-width: 1260px;
  padding-bottom: 15px;
  display: flex;
  flex-wrap: wrap;
  gap: 16px;

  a[href^="/product/"] {
    width: calc(20% - 13px);
  }

  ${media.desktop`
    a[href^="/product/"] {
         width: calc(33.3% - 12px);
    }
  `}
  ${media.tablet`
    max-width: 880px; 
    a[href^="/product/"] {
      width: calc(50% - 10px);
    }
  `}
`;
